import React from 'react';

import {BUTTON_COLOR, BUTTON_STYLE, ELEMENT_TYPE, FILTER_SECTION, FILTER_TYPE} from '../../../constants';
import {isSimulationMode} from '../canvas-timeline/canvas_timeline_util';
import HeaderBar, {buildThirdLevelNavHeader} from '../../../forecast-app/shared/components/headers/header-bar/header_bar';
import {handleLayoutDetailSelect} from './projects_scheduling_options';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';
import Util from '../../../forecast-app/shared/util/util';
import {FILTER_SECTIONS} from '../../../forecast-app/shared/components/filters/FilterWrapper';
import {getFiltersAlphabetically} from '../../../forecast-app/shared/components/filters/filter_util';
import {TopHeaderBar} from '../../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import {onCreateShare} from './share/CreateShare';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';
import {UTILIZATION_FORMAT} from '../canvas-timeline/canvas_timeline_util';

const onPrint = () => {
	dispatch(EVENT_ID.CANVAS_TIMELINE_PRINT);
};

const onFilterChange = (pageComponent, filterValues, filterFunctions) => {
	const filterVersionString = 'canvas-scheduling-filters-v4';
	const localStorageString = pageComponent.props.projectId
		? `${filterVersionString}-projects-projectId-${pageComponent.props.projectId}`
		: pageComponent.props.groupId
		? `${filterVersionString}-projects-groupId-${pageComponent.props.groupId}`
		: `${filterVersionString}-projects`;
	Util.localStorageSetItem(localStorageString, JSON.stringify(filterValues));
	pageComponent.setState({filters: filterValues, filterFunctions}, () => {
		dispatch(EVENT_ID.CANVAS_TIMELINE_FORCE_REDRAW, {preventFiltering: false});
	});
};

const mapFilterV4ViewerDataToGraphQLType = pageComponent => {
	const {projectFilters, personFilters, projectTimelineFilters} = pageComponent.getFilterData();

	return {
		filters: {edges: [...personFilters, ...projectFilters, ...projectTimelineFilters].map(filter => ({node: filter}))},
	};
};

export const getTopHeaderContent = (pageComponent, isProjectTimeline) => {
	const useThirdLevelNav = pageComponent
		.getFilterData()
		.viewer.availableFeatureFlags.some(flag => flag.key === 'third_level_nav');
	const showOnboardingContainer = pageComponent
		.getFilterData()
		.viewer.availableFeatureFlags.some(flag => flag.key === 'hide_onboarding_container');

	const content = [];
	if (showOnboardingContainer && useThirdLevelNav) {
		const onboardingFlows = [];
		onboardingFlows.push({
			id: 'schedule-project-introduction',
			title: 'Schedule (Project View) - Intro',
			description: null,
			contentId: '1594301287kBay8083',
		});
		if (hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS)) {
			onboardingFlows.push({
				id: 'schedule-project-capacity',
				title: 'Schedule (Project View) - Project Capacity',
				description: null,
				contentId: '1594302709jEjw0951',
			});
			onboardingFlows.push({
				id: 'schedule-project-team',
				title: 'Schedule (Project View) - Project Team and Milestones',
				description: null,
				contentId: '1594303634ySob0946',
			});
		}
		onboardingFlows.push({
			id: 'schedule-projects-filter',
			title: 'Filtering',
			description: null,
			contentId: '1610444655dQve1546',
		});

		const onboardingComponent = {
			id: 'onboarding-component',
			intl: pageComponent.props.intl,
			type: TopHeaderBar.TYPE.ONBOARDING,
			title: 'Learn to use the Schedule (Project View)',
			options: onboardingFlows,
			helpCenterLink: 'https://help.forecast.it/schedule-project-view-walkthrough',
		};
		if (!isProjectTimeline) {
			content.push(onboardingComponent);
		}
	}
	return content;
};

export const createHeader = (
	pageComponent,
	isProjectTimeline,
	projectTimelineCompanyProjectId = null,
	projectTimelineCompanyProjectGroupId = null,
	projectTimelineProjectId = null,
	projectTimelineProjectGroupId = null
) => {
	const {projectSchedulingDetailSelectorOptions} = pageComponent.state;
	const sharedContext = pageComponent.props.sharedContext;
	const useThirdLevelNav = pageComponent
		.getFilterData()
		.viewer.availableFeatureFlags.some(flag => flag.key === 'third_level_nav');

	const leftContent = [],
		rightContent = [];

	leftContent.push({
		type: ELEMENT_TYPE.EMPTY_SPACE,
		width: 14,
	});

	if (hasFeatureFlag("resource_utilization_format") && isProjectTimeline) {
		const {company} = pageComponent.getFilterData();
		if(!company.isUsingProjectAllocation) {
			const dropdownOptions = [
				{value: UTILIZATION_FORMAT.HOURS, label: pageComponent.props.intl.formatMessage({id: 'scheduling.heatmap_in_hours'})},
				{value: UTILIZATION_FORMAT.PERCENTAGE, label: pageComponent.props.intl.formatMessage({id: 'scheduling.heatmap_in_percentage'})},
			];

			leftContent.push({
				type: ELEMENT_TYPE.DROPDOWN,
				dropdownOptions,
				value: pageComponent.state.peopleSchedulingOptions.utilizationFormat,
				callback: format => pageComponent.handleUtilizationFormatChange(format.value),
				cy: 'utilization-resource-format-changer',
			});
		}
	}

	if (!sharedContext) {
		leftContent.push({
			type: ELEMENT_TYPE.THE_EYE,
			options: projectSchedulingDetailSelectorOptions,
			onSelect: selected => handleLayoutDetailSelect(pageComponent, selected),
			openRight: true,
			disableTooltip: true,
		});
		leftContent.push({
			type: ELEMENT_TYPE.DEPENDENCY_MODE_SCHEDULING,
			className:
				'dependency-mode-button ' +
				(pageComponent.state.isDraggingWholeDependencyChainModeOn
					? 'dependency-mode-whole-chain'
					: 'dependency-mode-single-task'),
			onClick: pageComponent.toggleDependencyDragMode.bind(pageComponent),
			title: pageComponent.props.intl.formatMessage({
				id: pageComponent.state.isDraggingWholeDependencyChainModeOn
					? 'scheduling.dependency_drag_mode_chain'
					: 'scheduling.dependency_drag_mode_single',
			}),
			cy: 'dependency-button',
		});
	}

	leftContent.push({
		type: ELEMENT_TYPE.EMPTY_SPACE,
		width: 93,
	});

	if (!sharedContext) {
		leftContent.push({type: ELEMENT_TYPE.EXPAND_ALL_CANVAS, cy: 'expand-all-button'});

		if (!isProjectTimeline) {
			leftContent.push({
				type: ELEMENT_TYPE.SHARE,
				userpilot: 'project-schedule-share',
				disabled: !hasPermission(PERMISSION_TYPE.PROJECT_SCHEDULING_SHARE_CREATE),
				onClick: () =>
					onCreateShare(
						pageComponent.state.filters,
						pageComponent.state.projectSchedulingDetailSelectorOptions,
						pageComponent.timeline.state.zoomLevel
					),
			});
		}
	}

	leftContent.push({
		type: ELEMENT_TYPE.DATE_CHANGER,
		onlyTodayButton: true,
		handleTodayButtonClick: pageComponent.scrollToToday.bind(pageComponent),
		handleMoveDateButtonClick: pageComponent.handleMoveDateButtonClick.bind(pageComponent),
	});

	leftContent.push({
		type: ELEMENT_TYPE.ZOOM_MENU,
	});

	if (
		pageComponent.state.projectTimelineProjectStatus &&
		(pageComponent.state.projectTimelineProjectStatus === 'DONE' ||
			pageComponent.state.projectTimelineProjectStatus === 'HALTED')
	) {
		leftContent.push({
			type: ELEMENT_TYPE.INDICATOR,
			status: pageComponent.state.projectTimelineProjectStatus,
		});
	}
	rightContent.push({
		type: ELEMENT_TYPE.SEARCH,
		value: pageComponent.state.searchFilterValue || '',
		onChange: pageComponent.onSearchChange.bind(pageComponent),
		cy: 'canvas-search',
	});
	if (!sharedContext) {
		const filterVersionString = 'canvas-scheduling-filters-v4';
		let localStorageString = `${filterVersionString}-projects`;
		if (pageComponent.props.groupId) {
			localStorageString += `-groupId-${pageComponent.props.groupId}`;
		} else if (pageComponent.props.projectId) {
			localStorageString += `-projectId-${pageComponent.props.projectId}`;
		}

		const {viewer, modules, company} = pageComponent.getFilterData();

		const hasFinancialModule = Util.hasFinance(modules);

		const projectFilters =
			isProjectTimeline && projectTimelineCompanyProjectId
				? []
				: [
						FILTER_TYPE.PROJECT,
						FILTER_TYPE.CLIENT,
						FILTER_TYPE.PROJECT_STAGE,
						FILTER_TYPE.PROJECT_STATUS,
						FILTER_TYPE.LABEL,
				  ];
		let personFilters =
			isProjectTimeline && !hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS)
				? undefined
				: [FILTER_TYPE.PERSON, FILTER_TYPE.ROLE, FILTER_TYPE.PERMISSION_LEVEL, FILTER_TYPE.LABEL];
		const taskFilters = [
			FILTER_TYPE.PERSON,
			FILTER_TYPE.ROLE,
			FILTER_TYPE.CLIENT_GUEST_USERS,
			FILTER_TYPE.INDICATOR,
			FILTER_TYPE.RECENT_ACTIVITY,
			FILTER_TYPE.LABEL,
		];

		const filterSection = isProjectTimeline ? FILTER_SECTION.PROJECT_TIMELINE : FILTER_SECTION.SCHEDULING_PROJECTS;

		if (personFilters && Util.hasDepartments(modules)) {
			personFilters.push(FILTER_TYPE.DEPARTMENT);
		}

		if (personFilters && Util.hasSkills() && hasFeatureFlag('skills')) {
			personFilters.push(FILTER_TYPE.SKILL);
		}

		const filterV4Viewer = mapFilterV4ViewerDataToGraphQLType(pageComponent);

		if (personFilters && !isProjectTimeline) {
			personFilters.push(FILTER_TYPE.INTERNAL_TIME);
			personFilters.push(FILTER_TYPE.TIME_OFF);
		}

		if (personFilters && isProjectTimeline) {
			personFilters.splice(0, 1, FILTER_TYPE.PROJECT_PERSON);
			personFilters.push(FILTER_TYPE.TIME_OFF);
		}

		if (isProjectTimeline) {
			taskFilters.splice(2, 0, FILTER_TYPE.PROJECT_PHASE);
			taskFilters.splice(0, 1, FILTER_TYPE.PROJECT_PERSON);
			taskFilters.push(FILTER_TYPE.PROJECT_OWNER);
			taskFilters.push(FILTER_TYPE.PROJECT_FOLLOWER);
			taskFilters.push(FILTER_TYPE.PROJECT_STATUS_COLUMN);
		} else {
			taskFilters.push(FILTER_TYPE.STATUS_CATEGORY);
			taskFilters.push(FILTER_TYPE.OWNER);
			taskFilters.push(FILTER_TYPE.FOLLOWER);
			personFilters.splice(1, 0, FILTER_TYPE.TEAM);
			if (projectFilters.length) {
				projectFilters.splice(2, 0, FILTER_TYPE.PERSON);
			}
		}

		if (hasFinancialModule && !projectTimelineCompanyProjectId) {
			// only show the budget type if company has financial access and is not simple project timeline. We still show the budget filter if connected project
			projectFilters.splice(1, 0, FILTER_TYPE.PROJECT_TYPE);
		}

		// No person filters if using task allocation and on project scheduling.
		if (!company.isUsingProjectAllocation && !isProjectTimeline) {
			personFilters = [];
		}

		const preselectedTab =
			isProjectTimeline && projectTimelineCompanyProjectId ? FILTER_SECTIONS.TASKS : FILTER_SECTIONS.PROJECTS;

		rightContent.push({
			type: ELEMENT_TYPE.FILTER_V4,
			defaultSection: preselectedTab,
			projectFilters,
			peopleFilters: personFilters,
			taskFilters: getFiltersAlphabetically(taskFilters, pageComponent.props.intl.formatMessage),
			viewer: filterV4Viewer,
			appliedFiltersName: localStorageString,
			filterSection,
			onFiltersChange: (filterValues, filterFunctions) => onFilterChange(pageComponent, filterValues, filterFunctions),
			projectId: projectTimelineProjectId,
			projectGroupId: projectTimelineProjectGroupId,
			companyProjectId: projectTimelineCompanyProjectId,
			companyProjectGroupId: projectTimelineCompanyProjectGroupId,
			cy: isProjectTimeline ? 'project-schedule-filters' : 'projects-schedule-filters',
		});

		if (isProjectTimeline) {
			const project = pageComponent
				.getFilterData()
				.projects.find(project => '' + project.companyProjectId === pageComponent.props.match.params.projectId);
			const timelineProject = isProjectTimeline && !pageComponent.props.match.params.groupId ? project : null;
			if (
				!isSimulationMode() &&
				project &&
				!project.isInProjectGroup &&
				hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS) &&
				timelineProject &&
				!['HALTED', 'DONE'].includes(timelineProject.status) &&
				!timelineProject.useManualAllocations
			) {
				const autoScheduleButton = {
					key: 'auto-schedule-button',
					type: ELEMENT_TYPE.BUTTON,
					customClassName: 'auto-scheduling-button',
					autoScheduleStyle: true,
					text: pageComponent.props.intl.formatMessage({id: 'auto_scheduling.auto_schedule'}),
					callback: pageComponent.showAutoSchedulingModal.bind(pageComponent),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.AI_BLUE,
					dataCy: 'auto-schedule-button',
				};
				rightContent.push(autoScheduleButton);
			}
		}
		if (
			!sharedContext &&
			viewer.availableFeatureFlags &&
			!useThirdLevelNav &&
			viewer.availableFeatureFlags.some(flag => flag.key === 'hide_onboarding_container')
		) {
			const onboardingFlows = [];
			onboardingFlows.push({
				id: 'schedule-project-introduction',
				title: 'Schedule (Project View) - Intro',
				description: null,
				contentId: '1594301287kBay8083',
			});
			if (hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS)) {
				onboardingFlows.push({
					id: 'schedule-project-capacity',
					title: 'Schedule (Project View) - Project Capacity',
					description: null,
					contentId: '1594302709jEjw0951',
				});
				onboardingFlows.push({
					id: 'schedule-project-team',
					title: 'Schedule (Project View) - Project Team and Milestones',
					description: null,
					contentId: '1594303634ySob0946',
				});
			}
			onboardingFlows.push({
				id: 'schedule-projects-filter',
				title: 'Filtering',
				description: null,
				contentId: '1610444655dQve1546',
			});

			const onboardingComponent = {
				id: 'onboarding-component',
				intl: pageComponent.props.intl,
				type: ELEMENT_TYPE.ONBOARDING_POPUP,
				title: 'Learn to use the Schedule (Project View)',
				options: onboardingFlows,
				helpCenterLink: 'https://help.forecast.it/schedule-project-view-walkthrough',
			};
			if (!isProjectTimeline) {
				rightContent.push(onboardingComponent);
			}
		}
	} else {
		rightContent.push({
			type: ELEMENT_TYPE.PRINT,
			onClick: onPrint,
		});
		rightContent.push({
			type: ELEMENT_TYPE.COPY_LINK,
			link: window.location.href,
		});
	}

	if (useThirdLevelNav) {
		return buildThirdLevelNavHeader(leftContent, rightContent, null);
	}
	return <HeaderBar scheduling={true} excludeLeftPadding={true} leftContent={leftContent} rightContent={rightContent} />;
};
